// ** React Imports
import { useContext, useRef, useCallback, useMemo } from "react";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";
import { getUserProfileMedia } from "store/modules/user-profile/actions/get-user-profile-media.action";

// ** Third Party Components
import { useTranslation } from "react-i18next";

// ** Custom Components
import Block from "components/layout/Block";
import Text from "components/typography/Text";
import Loader from "components/layout/Loader";
import MediaPostProfile from "components/user/MediaPosts/MediaPostProfile";
import { CardBody } from "components/layout/Card/CardBody";
import { CardWrapper } from "components/layout/Card/CardWrapper";

// ** Contexts
import authContext from "contexts/authContext";
import { StyledRightSection } from "../styles";

// #####################################################

const UserProfileMedia = ({ user }) => {
	const { t } = useTranslation(["userProfile", "common"]);

	const isAuth = useContext(authContext);
	const dispatch = useDispatch();

	const media = useSelector((state) => state.userProfileMainContent.media);

	const isMediaEmpty =
		media.loaded && (media.data?.length === 0 || !media.data?.length);

	const loadedMedia = media?.data?.length ?? 0;
	const totalMedia = media?.total ?? 0;

	const loadMoreMedia = () => {
		dispatch(
			getUserProfileMedia({
				userId: user.id,
				page: media.pagination.currentPage + 1,
				isAuth,
				hash: media.hash,
				infinityScroll: true,
			})
		);
	};

	const containerRef = useRef(null);
	const triggerObserver = useRef();
	const triggerRef = useCallback(
		(node) => {
			if (media.loading || !("IntersectionObserver" in window)) return;
			if (loadedMedia >= totalMedia) return null;
			if (triggerObserver.current) triggerObserver.current.disconnect();

			const marginValue =
				containerRef.current?.getBoundingClientRect().height * 0.15 ||
				600;

			triggerObserver.current = new IntersectionObserver(
				(entries) => {
					// "some" is used because adding rootMargin creates more intersections in the array. In this case, one for rootMargin and one for the div. Without this, the condition might not be entered when scrolling quickly.
					if (entries.some((entry) => entry.isIntersecting)) {
						loadMoreMedia();
					}
				},
				{ rootMargin: `0px 0px ${marginValue}px 0px` }
			);
			if (node) triggerObserver.current.observe(node);
		},
		[media.loading]
	);

	// #####################################################

	const memoizedMediaPostsProfile = useMemo(
		() =>
			media?.data.map((item, index) => {
				return (
					<MediaPostProfile
						key={`${item.media_type}_${item.id}`}
						item={item}
						user={user}
					/>
				);
			}),
		[media?.data, user]
	);

	if (isMediaEmpty) {
		return (
			<CardWrapper>
				<CardBody>
					<Block my={2}>
						<Text>{t("userProfile:noPosts")}</Text>
					</Block>
				</CardBody>
			</CardWrapper>
		);
	}

	return (
		<StyledRightSection ref={containerRef}>
			{memoizedMediaPostsProfile}

			{media?.total > media.data?.length &&
				totalMedia - loadedMedia > 0 &&
				media.loading && (
					<CardWrapper>
						<CardBody>
							<Block flex justifyCenter>
								<Loader />
							</Block>
						</CardBody>
					</CardWrapper>
				)}
			{media?.total > media.data?.length &&
				totalMedia - loadedMedia > 0 &&
				!media.loading &&
				!(
					media?.pagination?.currentPage > 0 &&
					media?.data?.length === 0
				) && (
					<div
						ref={triggerRef}
						style={{
							height: "1px",
							pointerEvents: "none",
						}}
					/>
				)}
		</StyledRightSection>
	);
};

// #####################################################

export default UserProfileMedia;

// #####################################################
